import { useTable, usePagination } from 'react-table'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import * as Sentry from '@sentry/node'
import { CClient, showErrorAlert } from '@common/utils'
import { useSessionContext } from '@contexts/session'
import Avatar from '@components/student-dashboard/gamification/avatar'
import Spinner from '@components/common/spinner'
import { CLASS_LEADERBOARD_PAGE_SIZE } from '@lib/constants/gamification'
import { StudentClassLeaderboardResponseType } from '@lib/types/leaderboard'
import Link from 'next/link'

type TableProps = {
  columns: (
    | {
        Header: () => JSX.Element
        Cell: ({
          row: { original },
        }: {
          row: {
            original: any
          }
        }) => JSX.Element
        accessor: string
      }
    | {
        Header: string
        accessor: string
        Cell?: JSX.Element
      }
  )[]
  data: {
    id: number
    gender: string
    name: string
    username: string
    rank: number
    grade: string
    points: number
  }[]
  fetchData: ({
    pageSize,
    pageIndex,
  }: {
    pageSize: number
    pageIndex: number
  }) => Promise<void>
  loading: boolean
  pageCount: number
}

const paginationButtonCount = 10
const paginationBreakPoint = 4

const Table = ({
  columns,
  data,
  fetchData,
  loading,
  pageCount: controlledPageCount,
}: TableProps) => {
  const { student } = useSessionContext()
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: CLASS_LEADERBOARD_PAGE_SIZE },
      manualPagination: true,
      pageCount: controlledPageCount,
    },
    usePagination
  )
  const [initialPaginationIndex, setInitialPaginationIndex] = useState(0)

  const paginationControl = () => {
    let finalIndex = 0
    if (pageIndex <= paginationBreakPoint) {
      finalIndex = 0
    } else if (pageIndex + 1 >= pageCount - paginationBreakPoint) {
      finalIndex =
        pageCount - paginationButtonCount < 0
          ? 0
          : pageCount - paginationButtonCount
    } else if (pageIndex > paginationBreakPoint) {
      finalIndex = pageIndex - paginationBreakPoint
    }

    setInitialPaginationIndex(finalIndex)
  }

  useEffect(() => {
    fetchData({ pageIndex, pageSize })
    paginationControl()
  }, [fetchData, pageIndex, pageSize])

  return (
    <>
      <div className="bg-white px-4 rounded-md overflow-x-auto my-8">
        {loading && (
          <div className="w-full flex justify-center pt-4">
            <Spinner classes="text-orange h-10 w-10" />
          </div>
        )}
        <table {...getTableProps()} className="w-full bg-white">
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <th
                    key={index}
                    {...column.getHeaderProps()}
                    className="font-500 pb-2 pt-4 text-grey-700"
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row)
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.original.id}
                  className={`border-t border-grey-200 rounded-lg ${
                    row.original.id === student?.id
                      ? 'bg-orange-200 border-orange-300'
                      : 'bg-white'
                  }`}
                >
                  {row.cells.map((cell, index) => {
                    return (
                      <td
                        key={index}
                        {...cell.getCellProps()}
                        className="p-3 text-center text-grey"
                      >
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 items-center justify-center pb-5">
        <div className="grid grid-cols-5 gap-5 md:grid-cols-10">
          {pageIndex !== 0 && (
            <>
              <button
                className={`py-2 px-4 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                  canPreviousPage ? 'bg-white' : 'bg-grey-500'
                }`}
                onClick={() => gotoPage(0)}
              >
                {'<<'}
              </button>{' '}
              <button
                className={`py-2 px-4 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                  canPreviousPage ? 'bg-white' : 'bg-grey-500'
                }`}
                onClick={() => previousPage()}
              >
                {'<'}
              </button>{' '}
            </>
          )}
          {pageCount > 0 &&
            [...Array(paginationButtonCount)].map(
              (value: undefined, index) =>
                initialPaginationIndex + index + 1 <= pageCount && (
                  <button
                    key={initialPaginationIndex + index + 1}
                    type="button"
                    onClick={() => gotoPage(initialPaginationIndex + index)}
                    className={`btn-orange shadow ${
                      pageIndex === initialPaginationIndex + index
                        ? 'bg-orange text-white'
                        : 'bg-white text-grey'
                    }  py-2 px-4 rounded-md font-500 outline-none`}
                  >
                    {initialPaginationIndex + index + 1}
                  </button>
                )
            )}

          {pageIndex !== pageCount - 1 && (
            <>
              <button
                className={`py-2 px-4 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                  canNextPage ? 'bg-white' : 'bg-grey-500'
                }`}
                onClick={() => nextPage()}
              >
                {'>'}
              </button>{' '}
              <button
                className={`py-2 px-4 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                  canNextPage ? 'bg-white' : 'bg-grey-500'
                }`}
                onClick={() => gotoPage(pageCount - 1)}
              >
                {'>>'}
              </button>{' '}
            </>
          )}
        </div>
        <div className="w-32 font-500 text-grey text-center lg:text-right">
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageCount}
          </strong>{' '}
        </div>
      </div>
    </>
  )
}

const QuizLeaderboardTable = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageCount, setPageCount] = useState(0)
  const fetchIdRef = useRef(0)
  const columns = useMemo(
    () => [
      {
        Header: () => <div className="text-left ml-4">Name</div>,
        Cell: ({ row: { original } }) => {
          return (
            <Link href={`/@${original?.username}`}>
              <a className="flex items-center space-x-2">
                <Avatar
                  image={original.avatar}
                  gender={original.gender}
                  className="w-8 h-8 rounded-full object-contain"
                  name={original.name}
                />
                <span className="hover:underline text-left">
                  {original.name}
                </span>
              </a>
            </Link>
          )
        },
        accessor: 'name',
      },
      {
        Header: 'Rank',
        accessor: 'rank',
      },
      {
        Header: 'Grade',
        accessor: 'grade',
      },
      {
        Header: 'Points',
        accessor: 'points',
      },
    ],
    []
  )

  const fetchData = useCallback(
    async ({
      pageSize,
      pageIndex,
    }: {
      pageSize: number
      pageIndex: number
    }) => {
      const fetchId = ++fetchIdRef.current
      setLoading(true)
      try {
        const currentPageNumber = pageIndex + 1
        const response = await CClient(
          `/api/v1/quiz-leaderboard/?page=${currentPageNumber}&page_size=${pageSize}`
        )

        if (response.ok) {
          const responseData: StudentClassLeaderboardResponseType = await response.json()
          const { count, results } = responseData
          const { leaderboard } = results
          const leaderboardData = leaderboard.map((data) => ({
            id: data.student.id,
            avatar: data.student.avatar,
            name: data.student.name,
            username: data.student?.username,
            grade: data.student.grade,
            gender: data.student.gender,
            rank: data.rank,
            points: data.points,
          }))
          setPageCount(Math.ceil(count / pageSize))
          if (fetchId === fetchIdRef.current) {
            setData(leaderboardData)
            setLoading(false)
          }
        } else {
          throw new Error(response.statusText)
        }
      } catch (error) {
        Sentry.captureException(error)
        setLoading(false)
        showErrorAlert({
          text:
            'There was an issue fetching leaderboard. Please refresh the page and try again or contact support for help with this issue.',
        })
      }
    },
    []
  )

  return (
    <Table
      columns={columns}
      data={data}
      fetchData={fetchData}
      loading={loading}
      pageCount={pageCount}
    />
  )
}

export default QuizLeaderboardTable
